import { enums, INVOICE_ITEM_TYPE_OPTIONS } from "@shared/constants";
import { PublicInternalAnalyticsTrackingSerializer } from "@shared/modules/analytics/types";
import { IBasket } from "@shared/modules/basket/interfaces/IBasket";
import { TicketBasketType } from "@shared/modules/basket/types";
import { isRawBasket } from "@shared/modules/basket/utils/helpers";
import { DataUtils } from "./DataUtils";
import { IInteractionDataBuilder } from "./IInteractionDataBuilder";

export type InitiateCheckoutDataBuilderData = {
  basket: TicketBasketType | IBasket;
  additionalParams?: Record<string, unknown>;
};

/**
 * Builder for tracking when users begin the checkout process.
 *
 * This is a critical conversion point that tracks:
 * - Which items are being purchased
 * - The venue/location of purchase
 * - The platform/source of the purchase
 * - For tickets, which events are being purchased
 *
 * Business Logic:
 * - We track both raw baskets (direct API data) and IBasket service objects
 * - Event IDs are extracted for conversion tracking
 * - Purchase source helps attribute sales to different platforms/channels
 * - Additional parameters can be added for special checkout flows
 */
export class InitiateCheckoutDataBuilder implements IInteractionDataBuilder {
  private data?: InitiateCheckoutDataBuilderData;
  private alternateLabel?: (typeof enums.INTERACTION_LABEL_MAP_BY_ID)[keyof typeof enums.INTERACTION_LABEL_MAP_BY_ID]["id"];

  constructor(
    data?: InitiateCheckoutDataBuilderData,
    alternateLabel?: (typeof enums.INTERACTION_LABEL_MAP_BY_ID)[keyof typeof enums.INTERACTION_LABEL_MAP_BY_ID]["id"]
  ) {
    this.alternateLabel = alternateLabel;
    this.data = data;
  }

  private static getMembershipIdsFromBasket(
    basket: TicketBasketType
  ): Array<number> {
    if (!basket.item_groups) {
      return [];
    }
    const membershipItemGroups = basket.item_groups.filter(
      (ig) =>
        ig.item_type ===
        INVOICE_ITEM_TYPE_OPTIONS.INVOICE_ITEM_TYPE_MEMBERSHIP.id
    );
    if (membershipItemGroups.length > 0) {
      return membershipItemGroups
        .map((mig) => mig.membership_group)
        .filter((value, index, self) => self.indexOf(value) === index);
    }
    return [];
  }

  /**
   * Check if the data can be logged. if the basket has not item groups, its empty and should not be logged.
   *
   * @param basket - The basket to check
   * @returns {boolean}
   */
  canLogData = (basket: TicketBasketType | IBasket): boolean => {
    const basketIsRaw = isRawBasket(basket);

    if (basketIsRaw && basket.item_groups?.length === 0) {
      return false;
    }

    if (!basketIsRaw && basket.itemGroups?.length === 0) {
      return false;
    }

    return true;
  };

  getRawBasketFields() {
    const basket = this.data?.basket as TicketBasketType;
    return {
      basket_id: basket.id,
      venue_id: basket.payment_venue,
      event_ids_for_conversions: DataUtils.getEventIdsFromBasket(basket),
      product_ids_for_conversions: DataUtils.getProductIdsFromBasket(basket),
      membership_ids_for_conversions:
        InitiateCheckoutDataBuilder.getMembershipIdsFromBasket(basket),
      custom_params: {
        purchase_source_platform: basket.purchase_source_platform,
        permission_ids: DataUtils.getPermissionIdsFromItemGroups(
          basket.item_groups
        ),
        ...this.data?.additionalParams,
      },
    };
  }

  getBasketServiceFields() {
    const basket = this.data?.basket as IBasket;
    return {
      basket_id: basket.basketId,
      venue_id: basket.paymentVenue,
      event_ids_for_conversions: basket.basketEventIds,
      product_ids_for_conversions: basket.basketProductIds,
      membership_ids_for_conversions: basket.basketMembershipIds,
      custom_params: {
        purchase_source_platform: basket.purchaseSourcePlatform,
        permission_ids: DataUtils.getPermissionIdsFromItemGroups(
          basket.itemGroups
        ),
        ...this.data?.additionalParams,
      },
    };
  }

  build(): PublicInternalAnalyticsTrackingSerializer | null {
    if (!this.data) {
      return null;
    }
    const { basket } = this.data;

    if (!this.canLogData(basket)) {
      return null;
    }

    const basketFields = isRawBasket(basket)
      ? this.getRawBasketFields()
      : this.getBasketServiceFields();
    return {
      label:
        this.alternateLabel ||
        enums.INTERACTION_LABEL_MAP_BY_ID.initiate_checkout.id,
      interaction_type: enums.INTERACTION_TYPE_MAP.INITIATE_CHECKOUT.id,
      ...basketFields,
    };
  }
}
